import React from "react";
import { connect } from "react-redux";

import { executeActions, getWidgetSpecificProps } from "@utils/index";
import { CommonProps } from "@utils/interface";
import { getInrFormat } from "@utils/inrFormatter";
import { resolvePath } from "@utils/resolvePath";
import {
  BannerContainer,
  BannerCopy,
  BannerHeader,
  BannerStrike,
  BannerTag,
  BannerTagLabel,
  BannerText,
  BannerTextSection,
  Chevron
} from "./styles";

const PA_OWNER_CAR = "pa_owner_car";
const RSA_COMBO_CAR = "rsa_outstation_keyprotect_combo_car";
const PA_FREE_SUM_FALLBACK = "15 lakh";

interface FreeAddon {
  id?: string;
  display_name?: string;
  is_selected?: boolean;
  sum_insured?: number | string | null;
  savings_amount?: number | string | null;
}

interface WidgetState {
  configuration?: {
    normalisedConfig?: Record<string, unknown>;
  };
}

interface AddonUpgrade {
  id?: string;
  display_name?: string;
  is_selected?: boolean;
  addon_selected?: boolean;
  current_sum_insured?: number | string | null;
  upgraded_sum_insured?: number | string | null;
  current_variant_label?: string | null;
  upgraded_variant_label?: string | null;
}

export interface FreeUpgradeModalData {
  show?: boolean;
  type?: string | null;
  free_addons?: FreeAddon[];
  addon_upgrades?: AddonUpgrade[];
}

interface RenewalFreeUpgradeBannerProps extends CommonProps {
  modalData?: FreeUpgradeModalData;
  actions?: {
    on_click?: unknown[];
  };
}

export interface StructuredBannerContent {
  tag?: string;
  header: string;
  text?: string;
  savingsAmount?: string;
}

const formatCurrency = (amount?: number | string | null) => {
  if (amount === undefined || amount === null || amount === "") {
    return "";
  }
  const formattedAmount = getInrFormat(amount);
  return formattedAmount ? `₹${formattedAmount}` : "";
};

const formatCoverAmount = (amount?: number | string | null) => {
  const numericAmount = Number(amount);
  if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
    return "";
  }
  if (numericAmount % 100000 === 0) {
    return `${numericAmount / 100000} lakh`;
  }
  return formatCurrency(amount).replace(/^₹/, "");
};

const coverLabel = (
  label?: string | null,
  amount?: number | string | null
) => {
  if (label && !/^\d+(\.\d+)?$/.test(String(label).trim())) {
    return String(label).replace(/^₹\s*/, "");
  }
  return (formatCoverAmount(amount) || String(label || "")).replace(/^₹\s*/, "");
};

export const getRenewalFreeUpgradeBannerContent = (
  modalData?: FreeUpgradeModalData
): StructuredBannerContent | null => {
  if (!modalData?.show) {
    return null;
  }

  const addonUpgrade = (modalData.addon_upgrades || [])[0];
  if (addonUpgrade?.id === RSA_COMBO_CAR) {
    return {
      tag: "Upgraded at no extra cost",
      header: "Extra Car protect - unlimited towing",
      text: "Previously capped at 40kms, now there’s no limit"
    };
  }

  if (addonUpgrade?.id === PA_OWNER_CAR) {
    const upgraded = coverLabel(
      addonUpgrade.upgraded_variant_label,
      addonUpgrade.upgraded_sum_insured
    );
    const current = coverLabel(
      addonUpgrade.current_variant_label,
      addonUpgrade.current_sum_insured
    );
    if (!upgraded) {
      return null;
    }
    return {
      tag: "Upgraded at no extra cost",
      header: `Personal Accident Coverage - ₹${upgraded}`,
      text: current ? `Previously covered for ₹${current}` : undefined
    };
  }

  const freeAddons = modalData.free_addons || [];
  const paFreeAddon = freeAddons.find((addon) => addon.id === PA_OWNER_CAR);
  if (paFreeAddon) {
    const sumInsured =
      coverLabel(null, paFreeAddon.sum_insured) || PA_FREE_SUM_FALLBACK;
    return {
      tag: "Added FREE",
      header: `Personal Accident Cover - ₹${sumInsured} Sum Insured`
    };
  }

  const freeAddon = freeAddons[0];
  const addonName = freeAddon?.display_name;
  if (!addonName) {
    return null;
  }

  return {
    header: `${addonName} added for FREE`,
    savingsAmount: formatCurrency(freeAddon.savings_amount)
  };
};

const mapStateToProps = function (state: WidgetState, ownProps: CommonProps) {
  return getWidgetSpecificProps(
    state.configuration?.normalisedConfig,
    ownProps.id || ""
  );
};

const RenewalFreeUpgradeBanner = (props: RenewalFreeUpgradeBannerProps) => {
  const { id, is_visible, styles, modalData, actions } = props || {};
  const content = getRenewalFreeUpgradeBannerContent(modalData);
  const onClickActions = actions?.on_click || [];

  if ((id && !is_visible) || !modalData?.show || !content) {
    return null;
  }

  const handleClick = () => {
    executeActions(onClickActions);
  };

  return (
    <BannerContainer
      role="button"
      tabIndex={0}
      styles={styles}
      onClick={handleClick}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          handleClick();
        }
      }}
    >
      <BannerCopy>
        {content.tag && (
          <BannerTag $upgrade={content.tag === "Upgraded at no extra cost"}>
            <BannerTagLabel $upgrade={content.tag === "Upgraded at no extra cost"}>
              {content.tag}
            </BannerTagLabel>
          </BannerTag>
        )}
        <BannerTextSection>
          <BannerHeader>
            {content.header}
            {content.savingsAmount ? (
              <>
                {" "}
                <BannerStrike>{content.savingsAmount}</BannerStrike>
              </>
            ) : null}
          </BannerHeader>
          {content.text && <BannerText>{content.text}</BannerText>}
        </BannerTextSection>
      </BannerCopy>
      <Chevron
        src={resolvePath("/images/Chevron_right.svg")}
        alt=""
        aria-hidden="true"
      />
    </BannerContainer>
  );
};

export default connect(mapStateToProps)(RenewalFreeUpgradeBanner);
